import { useEffect, useState } from "react";
import QRCode from "qrcode";
import {
  Download,
  ExternalLink,
  Copy,
  Nfc,
  QrCode,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const copyText = async (text, message) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
  toast.success(message);
};

const pill =
  "flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border border-[#E7E2D7] text-[#57534E] hover:border-[#C25E38]/40 hover:text-[#C25E38] transition-colors duration-200 cursor-pointer bg-white/70";

export const RestaurantAccess = ({ restaurant }) => {
  const reviewUrl = `${window.location.origin}/review/${restaurant.slug}`;
  const [qr, setQr] = useState(null);

  useEffect(() => {
    let active = true;
    QRCode.toDataURL(reviewUrl, {
      width: 1024,
      margin: 2,
      color: { dark: "#1C1917", light: "#FFFFFF" },
    })
      .then((dataUrl) => active && setQr(dataUrl))
      .catch(() => toast.error("No se ha podido generar el QR."));
    return () => {
      active = false;
    };
  }, [reviewUrl]);

  const downloadQr = () => {
    if (!qr) return;
    const a = document.createElement("a");
    a.href = qr;
    a.download = `qr-${restaurant.slug}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div
      className="border-t border-[#E7E2D7] pt-4 flex flex-col gap-6"
      data-testid={`access-section-${restaurant.slug}`}
    >
      <div className="flex flex-col gap-3">
        <h3 className="font-serif-display text-base font-medium text-[#1C1917] flex items-center gap-2">
          <QrCode className="h-4 w-4 text-[#C25E38]" /> Acceso a valoraciones
        </h3>
        <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-[#B38D46]">
          {restaurant.name}
        </p>
        <p
          className="text-xs text-[#57534E] break-all"
          data-testid={`review-url-${restaurant.slug}`}
        >
          {reviewUrl}
        </p>

        <div className="flex items-center gap-4 flex-wrap">
          {qr ? (
            <img
              src={qr}
              alt={`QR de valoración de ${restaurant.name}`}
              data-testid={`qr-image-${restaurant.slug}`}
              className="h-32 w-32 rounded-xl border border-[#E7E2D7] bg-white p-2"
            />
          ) : (
            <div className="h-32 w-32 rounded-xl border border-dashed border-[#E7E2D7] flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-[#A8A29E]" />
            </div>
          )}
          <div className="flex flex-col items-start gap-2">
            <button
              type="button"
              onClick={downloadQr}
              disabled={!qr}
              data-testid={`download-qr-${restaurant.slug}`}
              className={`${pill} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <Download className="h-3 w-3" /> Descargar QR
            </button>
            <a
              href={reviewUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`open-review-access-${restaurant.slug}`}
              className={pill}
            >
              <ExternalLink className="h-3 w-3" /> Abrir valoración
            </a>
            <button
              type="button"
              onClick={() => copyText(reviewUrl, "URL copiada al portapapeles.")}
              data-testid={`copy-url-${restaurant.slug}`}
              className={pill}
            >
              <Copy className="h-3 w-3" /> Copiar URL
            </button>
          </div>
        </div>
      </div>

      <div
        className="flex flex-col gap-2.5"
        data-testid={`nfc-section-${restaurant.slug}`}
      >
        <h3 className="font-serif-display text-base font-medium text-[#1C1917] flex items-center gap-2">
          <Nfc className="h-4 w-4 text-[#C25E38]" /> Configuración NFC
        </h3>
        <p className="font-mono-label text-[10px] uppercase tracking-[0.25em] text-[#A8A29E]">
          NFC URL
        </p>
        <p
          className="text-xs text-[#57534E] break-all bg-white/70 border border-[#E7E2D7] rounded-xl px-3.5 py-2.5 font-mono-label"
          data-testid={`nfc-url-${restaurant.slug}`}
        >
          {reviewUrl}
        </p>
        <div>
          <button
            type="button"
            onClick={() => copyText(reviewUrl, "URL NFC copiada al portapapeles.")}
            data-testid={`copy-nfc-url-${restaurant.slug}`}
            className={pill}
          >
            <Copy className="h-3 w-3" /> Copiar URL NFC
          </button>
        </div>
        <p className="text-xs text-[#A8A29E] leading-relaxed">
          Programa esta URL en tu etiqueta NFC con un móvil o una herramienta
          de escritura NFC. Abrirá la misma página de valoración que el QR.
        </p>
      </div>
    </div>
  );
};
